import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { fetchSignalHistory } from "@/state/slices/signalSlice";

/** Fetch bot_all's closed-trade history for the signal pane's entry/exit
 *  markers. Mirrors `useTradingHistoryAutoFetch` but for the shared read-only
 *  signal source, and only while the pane is actually open + the source online
 *  (no point pulling history for a pane nobody's looking at). Refetches when the
 *  open-position count drops (a close event adds a history row). */
export function useSignalHistoryAutoFetch(): void {
  const dispatch = useAppDispatch();
  const active = useAppSelector(
    (s) => s.ui.signalChartOpen && s.signal.enabled && s.signal.online,
  );
  const positionCount = useAppSelector((s) => s.signal.positions.length);
  const prevCountRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active) return;
    void dispatch(fetchSignalHistory());
  }, [dispatch, active]);

  useEffect(() => {
    if (!active) return;
    const prev = prevCountRef.current;
    prevCountRef.current = positionCount;
    if (prev === null) return;
    if (positionCount < prev) {
      void dispatch(fetchSignalHistory());
    }
  }, [dispatch, active, positionCount]);
}
